import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  StyleSheet,
  Platform,
} from 'react-native';

import { t } from '../../i18n/translations';
import { useGuestUser } from '../../shared/contexts/GuestUserContext';
import CollapsibleIntroHeader from '../../shared/ui/CollapsibleIntroHeader';
import { iqesColors } from '../../shared/ui/iqesDesignSystem';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { useLanguageStore } from '../quiz-core/languageStore';
import { useUserProfile } from '../quiz-core/useStore';

import CreateFriendsGroupModal from './CreateFriendsGroupModal';
import FriendsCard from './FriendsCard';
import FriendsMembersModal from './FriendsMembersModal';
import JoinFriendsByInviteCodeModal from './JoinFriendsByInviteCodeModal';
import { useFriendsData } from './useFriendsData';
import { useFriendsInvitations } from './useFriendsInvitations';

interface FriendGroupSummary {
  id: string;
  name?: string;
  invite_code?: string;
}

interface FriendMembershipEntry {
  id: string;
  user_id: string;
  role?: string;
}

interface PendingInvite {
  id: string;
  sender_name?: string;
  group_name?: string;
}

interface FriendsHubRouteParamList {
  FamilyHub: undefined;
  MultiplayerLobby: {
    friendsGroupId: string;
    friendsGroupName?: string;
    groupType: 'friends';
  };
}

interface FriendsDataState {
  groups: Array<FriendGroupSummary & { owner_id?: string }>;
  membersByGroup: Record<string, FriendMembershipEntry[]>;
  invitations: PendingInvite[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface FriendsInvitationState {
  pendingInvites: PendingInvite[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  acceptInvite: (id: string) => Promise<void>;
  declineInvite: (id: string) => Promise<void>;
}

type FriendsHubNavigation = StackNavigationProp<FriendsHubRouteParamList>;

const FriendsHubScreen: React.FC = () => {
  const { language } = useLanguageStore();
  const navigation = useNavigation<FriendsHubNavigation>();
  const userProfile = useUserProfile();
  const { isGuest, endGuestSession } = useGuestUser();
  const {
    groups,
    membersByGroup,
    invitations,
    loading,
    error,
    refetch,
  } = useFriendsData(userProfile?.id || null) as unknown as FriendsDataState;
  const {
    pendingInvites,
    loading: invitesLoading,
    error: invitesError,
    refetch: refetchInvites,
    acceptInvite,
    declineInvite,
  } = useFriendsInvitations(userProfile?.id || null) as unknown as FriendsInvitationState;

  // Use invitations from useFriendsData if useFriendsInvitations doesn't provide them
  const actualPendingInvites = (pendingInvites || invitations || []) as PendingInvite[];

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [joinModalVisible, setJoinModalVisible] = useState(false);
  const [membersModalVisible, setMembersModalVisible] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');

  // Collapsible header state
  const [isHeaderExpanded, setIsHeaderExpanded] = React.useState(true);
  const [isInteracting, setIsInteracting] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'family' | 'friends'>('friends');

  // Multiplay tabs configuration - Only Family and Friends
  const multiplayTabs = [
    { id: 'family', label: t('familiesFriends.tabFamily', language), icon: 'home', colors: ['#3B82F6', '#1D4ED8'] },
    { id: 'friends', label: t('familiesFriends.tabFriends', language), icon: 'people', colors: ['#10B981', '#059669'] }
  ];

  // Navigation handlers for multiplay tabs - Only Family and Friends
  const handleTabChange = (tabId: string) => {
    if (tabId === 'family') {
      navigation.navigate('FamilyHub');
    } else if (tabId === 'friends') {
      setActiveTab('friends');
    }
  };

  // Auto-collapse timer logic using useRef to avoid circular dependencies
  const autoCollapseTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const clearAutoCollapseTimer = React.useCallback(() => {
    if (autoCollapseTimerRef.current) {
      clearTimeout(autoCollapseTimerRef.current);
      autoCollapseTimerRef.current = null;
    }
  }, []);

  const startAutoCollapseTimer = React.useCallback(() => {
    clearAutoCollapseTimer();

    if (isHeaderExpanded && !isInteracting) {
      autoCollapseTimerRef.current = setTimeout(() => {
        setIsHeaderExpanded(false);
      }, 6000); // 6 seconds
    }
  }, [isHeaderExpanded, isInteracting, clearAutoCollapseTimer]);

  // Start timer when header expands or interaction state changes
  React.useEffect(() => {
    startAutoCollapseTimer();
    return () => clearAutoCollapseTimer();
  }, [startAutoCollapseTimer, clearAutoCollapseTimer]);

  // Handle interaction events
  const handleInteractionStart = React.useCallback(() => {
    setIsInteracting(true);
  }, []);

  const handleInteractionEnd = React.useCallback(() => {
    setIsInteracting(false);
  }, []);

  const handleToggleHeader = React.useCallback(() => {
    setIsHeaderExpanded(prev => !prev);
  }, []);

  const handleCreateGroup = () => {
    setCreateModalVisible(true);
  };

  const handleJoinGroup = () => {
    setJoinModalVisible(true);
  };

  const handleViewMembers = (groupId: string) => {
    setSelectedGroupId(groupId);
    setMembersModalVisible(true);
  };

  const handleEnterLobby = (group: FriendGroupSummary) => {
    navigation.navigate('MultiplayerLobby', {
      friendsGroupId: group.id,
      friendsGroupName: group.name,
      groupType: 'friends'
    });
  };

  const handleShareInviteCode = async (group: FriendGroupSummary) => {
    if (group.invite_code) {
      Alert.alert(
        t('familiesFriends.titleInviteCode', language),
        t('familiesFriends.messageShareCodeWithFriends', language).replace('{{code}}', group.invite_code),
        [
          { text: t('familiesFriends.buttonOk', language), style: 'default' }
        ]
      );
    }
  };

  const handleModalSuccess = () => {
    refetch();
    refetchInvites();
  };

  const isLoading = loading || invitesLoading;
  const hasError = error || invitesError;

  if (isGuest || !userProfile?.id) {
    return (
      <View style={[premiumStyles.container, { backgroundColor: iqesColors.canvas }]}>
        <View style={premiumStyles.centeredContainer}>
          <Ionicons name="people-circle-outline" size={72} color={iqesColors.goldBright} />
          <Text style={[premiumStyles.errorTitle, { color: iqesColors.ivory }]}>{t('guestMode.multiplayerRequiredTitle', language)}</Text>
          <Text style={[premiumStyles.errorText, { color: 'rgba(255,248,232,0.76)' }]}>
            {t('guestMode.multiplayerRequiredMessage', language) || t('familiesFriends.signInToJoinFamily', language)}
          </Text>
          <PrimaryButton
            title={t('guestMode.signUpButton', language)}
            onPress={endGuestSession}
          />
        </View>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={[premiumStyles.container, { backgroundColor: iqesColors.canvas }]}>
        <View style={premiumStyles.centeredContainer}>
          <ActivityIndicator size="large" color={iqesColors.goldBright} />
          <Text style={[premiumStyles.loadingText, { color: iqesColors.goldSoft }]}>{t('familiesFriends.loadingFriendsHub', language)}</Text>
        </View>
      </View>
    );
  }

  if (hasError) {
    return (
      <View style={[premiumStyles.container, { backgroundColor: iqesColors.canvas }]}>
        <View style={premiumStyles.centeredContainer}>
          <Text style={[premiumStyles.errorTitle, { color: iqesColors.ivory }]}>{t('familiesFriends.errorLoadingFriendsHub', language)}</Text>
          <Text style={[premiumStyles.errorText, { color: 'rgba(255,248,232,0.76)' }]}>{error || invitesError}</Text>
          <PrimaryButton title={t('familiesFriends.buttonRetry', language)} onPress={refetch} />
        </View>
      </View>
    );
  }

  return (
    <View style={[premiumStyles.container, { backgroundColor: iqesColors.canvas }]}>
      {/* Unified Multiplay Header */}
      <CollapsibleIntroHeader
        title={t('familiesFriends.headerTitle', language)}
        subtitle={t('familiesFriends.headerSubtitle', language)}
        tabs={multiplayTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isExpanded={isHeaderExpanded}
        onToggle={handleToggleHeader}
        onInteractionStart={handleInteractionStart}
        onInteractionEnd={handleInteractionEnd}
      />

      <ScrollView
        style={premiumStyles.scrollView}
        contentContainerStyle={premiumStyles.contentContainer}
        showsVerticalScrollIndicator={false}
      >

        <View style={premiumStyles.premiumContentContainer}>
          <PrimaryButton title={t('familiesFriends.titleCreateFriendsGroup', language)} onPress={handleCreateGroup} />
          <PrimaryButton title={t('familiesFriends.buttonJoinWithCode', language)} onPress={handleJoinGroup} style={premiumStyles.buttonSpacing} />
        </View>

        {/* Pending Invitations - Simple display without InvitationCard */}
        {actualPendingInvites.length > 0 && (
          <View style={premiumStyles.section}>
            <Text style={premiumStyles.sectionTitle}>{t('familiesFriends.sectionTitlePendingInvitations', language)}</Text>
            {actualPendingInvites.map((invitation) => (
              <View key={invitation.id} style={premiumStyles.invitationItem}>
                <Text style={premiumStyles.invitationText}>
                  {t('familiesFriends.invitationFromTo', language).replace('{{sender}}', invitation.sender_name || t('familiesFriends.unknown', language)).replace('{{group}}', invitation.group_name || t('familiesFriends.group', language))}
                </Text>
                <View style={premiumStyles.invitationActions}>
                  <TouchableOpacity
                    style={[premiumStyles.actionButton, premiumStyles.acceptButton]}
                    onPress={() => acceptInvite(invitation.id)}
                  >
                    <Text style={premiumStyles.acceptButtonText}>{t('familiesFriends.actionAccept', language)}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={premiumStyles.declineButton}
                    onPress={() => declineInvite(invitation.id)}
                  >
                    <Text style={premiumStyles.declineButtonText}>{t('familiesFriends.actionDecline', language)}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Friends Groups */}
        <View style={premiumStyles.section}>
          <Text style={premiumStyles.sectionTitle}>{t('familiesFriends.sectionTitleYourFriendsGroups', language)}</Text>
          {groups.length === 0 ? (
            <View style={premiumStyles.emptyState}>
              <Ionicons name="people-outline" size={64} color={iqesColors.goldSoft} />
              <Text style={premiumStyles.emptyStateTitle}>{t('familiesFriends.emptyFriendsGroupsTitle', language)}</Text>
              <Text style={premiumStyles.emptyStateSubtext}>
                {t('familiesFriends.emptyFriendsGroupsSubtitle', language)}
              </Text>
            </View>
          ) : (
            groups.map((group) => {
              const members = membersByGroup[group.id] || [];
              const userMember = members.find((m: FriendMembershipEntry) => m.user_id === userProfile?.id);
              const userRole = userMember?.role || 'member';

              return (
                <FriendsCard
                  key={group.id}
                  group={group}
                  members={members}
                  userRole={userRole}
                  onShareLink={() => handleShareInviteCode(group)}
                  onShareCode={() => handleShareInviteCode(group)}
                  onLeaveGroup={() => Alert.alert(t('familiesFriends.leaveGroup', language), t('familiesFriends.featureNotImplemented', language))}
                  onShowMembers={() => handleViewMembers(group.id)}
                  onGoToLobby={handleEnterLobby}
                  styles={{}}
                />
              );
            })
          )}
        </View>
      </ScrollView>

      {/* Modals */}
      <CreateFriendsGroupModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onCreated={handleModalSuccess}
      />

      <JoinFriendsByInviteCodeModal
        visible={joinModalVisible}
        onClose={() => setJoinModalVisible(false)}
        onJoined={handleModalSuccess}
      />

      <FriendsMembersModal
        visible={membersModalVisible}
        onClose={() => setMembersModalVisible(false)}
        members={membersByGroup[selectedGroupId] || []}
        currentUserId={userProfile?.id || ''}
        isAdmin={(groups as Array<FriendGroupSummary & { owner_id?: string }>).find((g) => g.id === selectedGroupId)?.owner_id === userProfile?.id || false}
      />
    </View>
  );
};

const premiumStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 40,
  },
  centeredContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  loadingText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#F6BE3A',
    marginTop: 16,
    textAlign: 'center',
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFF7DA',
    textAlign: 'center',
    marginBottom: 8,
  },
  errorText: {
    fontSize: 16,
    color: 'rgba(255,248,232,0.74)',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: 24,
  },
  headerBadge: {
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 25,
    borderWidth: 1,
    borderColor: 'rgba(79, 70, 229, 0.2)',
  },
  headerText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1D1A23',
    letterSpacing: 1,
    textAlign: 'center',
  },
  tabRow: {
    flexDirection: 'row',
    marginBottom: 24,
    backgroundColor: '#F3F2F8',
    borderRadius: 12,
    padding: 4,
  },
  tabButton: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  activeTabButton: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  tabGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  tabText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1D1A23',
    marginLeft: 8,
  },
  inactiveTabContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  inactiveTabText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#777587',
    marginLeft: 8,
  },
  actionButtonsContainer: {
    flexDirection: 'column',
    gap: 12,
    marginBottom: 32,
    paddingHorizontal: 20,
  },
  actionButton: {
    width: '100%',
    borderRadius: 16,
    overflow: 'hidden',
    transform: [{ scale: 0.98 }],
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 4px 12px rgba(29, 26, 35, 0.08)',
      },
    }),
  },
  actionButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
    paddingHorizontal: 24,
    borderRadius: 16,
    position: 'relative',
    minHeight: 56,
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '900',
    color: '#FFFFFF',
    marginLeft: 10,
    textAlign: 'center',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF7DA',
    marginBottom: 16,
    textAlign: 'center',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
    paddingHorizontal: 24,
  },
  emptyStateTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#FFF7DA',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 16,
    color: 'rgba(255,248,232,0.74)',
    textAlign: 'center',
    lineHeight: 24,
  },
  invitationItem: {
    backgroundColor: '#080A18',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: 'rgba(246,190,58,0.38)',
    ...Platform.select({
      ios: {
        shadowColor: '#F6BE3A',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.14,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0px 2px 4px rgba(29, 26, 35, 0.06)',
      },
    }),
  },
  invitationText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFF7DA',
    marginBottom: 12,
  },
  invitationActions: {
    flexDirection: 'row',
    gap: 8,
  },
  acceptButton: {
    flex: 1,
    backgroundColor: '#10B981',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  acceptButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  declineButton: {
    flex: 1,
    backgroundColor: '#EF4444',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  declineButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  // Premium Design System Styles (matching Family Hub)
  premiumHeaderContainer: {
    alignItems: 'center',
    marginBottom: 32,
    paddingHorizontal: 20,
    paddingTop: 16,
  },
  premiumHeaderBadge: {
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    borderRadius: 25,
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(79, 70, 229, 0.2)',
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 4px 12px rgba(29, 26, 35, 0.08)',
      },
    }),
  },
  premiumHeaderText: {
    color: '#1D1A23',
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 1,
  },
  premiumSubtitle: {
    color: '#464555',
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 22,
    paddingHorizontal: 16,
    marginBottom: 20,
  },
  premiumTabContainer: {
    flexDirection: 'row',
    marginBottom: 32,
    paddingHorizontal: 20,
    paddingVertical: 8,
    gap: 12,
    marginTop: 4,
  },
  premiumTabButton: {
    flex: 1,
    borderRadius: 16,
    overflow: 'hidden',
    transform: [{ scale: 0.98 }],
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0px 2px 8px rgba(29, 26, 35, 0.06)',
      },
    }),
  },
  premiumTabActive: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    backgroundColor: '#4F46E5',
    borderWidth: 1.5,
    borderColor: '#4F46E5',
    borderRadius: 16,
  },
  premiumTabInactive: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 16,
  },
  premiumTabTextActive: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  premiumTabTextInactive: {
    color: '#777587',
    fontSize: 16,
    fontWeight: '600',
  },
  premiumTabContentRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  premiumTabIconContainer: {
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    borderRadius: 8,
    padding: 4,
    marginRight: 8,
  },
  premiumTabIconInactive: {
    backgroundColor: 'rgba(119, 117, 135, 0.08)',
  },
  premiumTabIcon: {
    fontSize: 18,
    color: '#4F46E5',
  },
  premiumTabIconTextInactive: {
    fontSize: 18,
    color: '#777587',
  },
  premiumContentContainer: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  buttonSpacing: {
    marginTop: 16,
  },
});

export default FriendsHubScreen;
